import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core'

enum Directions {
    top = 'top',
    bottom = 'bottom',
    left = 'left',
    right = 'right',
}

@Directive({
    selector: '[appTooltip]',
    standalone: false,
})
export class TooltipDirective implements OnDestroy {
    @Input() public appTooltip = ''
    private myPopup = null
    private timer

    @HostListener('window:scroll')
    onWindowScroll() {
        this.ngOnDestroy()
    }

    constructor(private elementRef: ElementRef) {}

    public ngOnDestroy(): void {
        if (this.myPopup) {
            this.myPopup.remove()
        }
    }

    @HostListener('mouseenter') onMouseEnter() {
        this.timer = setTimeout(() => {
            this.createTooltipPopup(this.elementRef.nativeElement.getBoundingClientRect())
        }, 100)
    }

    @HostListener('mouseleave') onMouseLeave() {
        if (this.timer) {
            clearTimeout(this.timer)
        }
        if (this.myPopup) {
            this.myPopup.remove()
            this.myPopup = null
        }
    }

    private createTooltipPopup(rect: any) {
        if (this.myPopup !== null) {
            return
        }

        const triggerX = rect.left
        const triggerY = rect.top
        const triggerWidth = rect.width
        const triggerHeight = rect.height
        const pageMargin = 10
        const arrowSize = 10

        // Create tooltip element
        const popup = document.createElement('div')
        popup.innerHTML = this.appTooltip
        popup.setAttribute('class', 'tooltip-container')
        document.body.appendChild(popup)

        const popupWidth = popup.getBoundingClientRect().width
        const popupHeight = popup.getBoundingClientRect().height

        // Determine best direction based on available space
        const spaceAbove = triggerY - pageMargin
        const spaceBelow = window.innerHeight - (triggerY + triggerHeight) - pageMargin
        const spaceLeft = triggerX - pageMargin
        const spaceRight = window.innerWidth - (triggerX + triggerWidth) - pageMargin

        let direction: Directions
        if (spaceAbove >= popupHeight + arrowSize) {
            direction = Directions.top
        } else if (spaceBelow >= popupHeight + arrowSize) {
            direction = Directions.bottom
        } else if (spaceLeft >= popupWidth + arrowSize) {
            direction = Directions.left
        } else if (spaceRight >= popupWidth + arrowSize) {
            direction = Directions.right
        } else {
            // Not enough space anywhere, pick the side with most space
            const maxVertical = Math.max(spaceAbove, spaceBelow)
            const maxHorizontal = Math.max(spaceLeft, spaceRight)
            if (maxVertical >= maxHorizontal) {
                direction = spaceAbove >= spaceBelow ? Directions.top : Directions.bottom
            } else {
                direction = spaceLeft >= spaceRight ? Directions.left : Directions.right
            }
        }

        popup.setAttribute('class', `tooltip-container ${direction}`)

        // Calculate ideal position based on direction
        let idealX = 0
        let idealY = 0

        switch (direction) {
            case Directions.top:
                idealX = triggerX + triggerWidth / 2 - popupWidth / 2
                idealY = triggerY - popupHeight - arrowSize
                break
            case Directions.bottom:
                idealX = triggerX + triggerWidth / 2 - popupWidth / 2
                idealY = triggerY + triggerHeight + arrowSize
                break
            case Directions.left:
                idealX = triggerX - popupWidth - arrowSize
                idealY = triggerY + triggerHeight / 2 - popupHeight / 2
                break
            case Directions.right:
                idealX = triggerX + triggerWidth + arrowSize
                idealY = triggerY + triggerHeight / 2 - popupHeight / 2
                break
        }

        // Clamp position to stay within viewport
        const maxX = window.innerWidth - popupWidth - pageMargin
        const maxY = window.innerHeight - popupHeight - pageMargin
        const finalX = Math.max(pageMargin, Math.min(idealX, maxX))
        const finalY = Math.max(pageMargin, Math.min(idealY, maxY))

        // Calculate how much we shifted from ideal position (for arrow adjustment)
        const shiftX = idealX - finalX
        const shiftY = idealY - finalY

        // Apply position
        popup.style.top = finalY + 'px'
        popup.style.left = finalX + 'px'

        // Set CSS custom properties for arrow positioning
        if (direction === Directions.top || direction === Directions.bottom) {
            popup.style.setProperty('--arrow-offset-x', shiftX + 'px')
        } else {
            popup.style.setProperty('--arrow-offset-y', shiftY + 'px')
        }

        this.myPopup = popup
    }
}
